import './TenantEditPage.css'
import { BasicPage } from '@/components/layout/BasicPage'
import { useParams, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { tenantGetSpec, tenantSaveSpec, tenantDeleteSpec, type TenantRow, UserRole, TenantUserRole } from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { Button } from '@/components/action/Button'
import { Notice } from '@/components/action/Notice'
import { TextBox } from '@/components/scalar/TextBox'
import { TimezoneBox } from '@/components/scalar/TimezoneBox'
import { routes } from '@/utilities/routes'
import { toastError, toastSuccess, toastWarning } from '@/components/action/Toaster'
import { TenantInvitations } from './TenantInvitations'
import { TenantMembers } from './TenantMembers'

export function TenantEditPage() {
  const { tenant: tenantId } = useParams<{ tenant: string }>()
  const api = useApi({ requireLogin: true })
  const navigate = useNavigate()

  const userRole = api.status.session?.userRole
  const tenantUserRole = api.status.session?.tenantUserRole
  const canEdit =
    userRole === UserRole.Admin ||
    tenantUserRole === TenantUserRole.Owner ||
    tenantUserRole === TenantUserRole.Admin
  const canDelete = userRole === UserRole.Admin

  const [tenant, setTenant] = useState<TenantRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState('')

  //--------------------------------------------------------------------------------
  const loadTenant = useCallback((value: TenantRow) => {
    setTenant(value)
    setName(value.name ?? '')
    setTimezone(value.timezone ?? 'America/New_York')
  }, [])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    async function fn() {
      if (!tenantId) return
      if (tenantId === 'new') {
        loadTenant({
          name: '',
          timezone: 'America/New_York',
        })
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const res = await api.request(tenantGetSpec, {
          path: { tenant: tenantId },
        })
        if (res.status === 200 && res.data?.tenant) {
          loadTenant(res.data.tenant)
        } else {
          toastError(res.error ?? 'Failed to load tenant')
        }
      } catch {
        toastError('Failed to load tenant')
      } finally {
        setLoading(false)
      }
    }
    void fn()
  }, [tenantId, loadTenant])

  //--------------------------------------------------------------------------------
  async function handleSave(action?: 'enable' | 'disable') {
    if (!tenantId || !tenant) return
    const disabled =
      action === 'enable' ? false : action === 'disable' ? true : (tenant.disabled ?? false)
    const payload: TenantRow = {
      ...tenant,
      name: name || undefined,
      timezone: timezone || undefined,
      disabled,
    }
    const res = await api.request(tenantSaveSpec, {
      path: { tenant: tenantId },
      body: { tenant: payload },
    })
    if (res.status === 200 && res.data?.tenant) {
      const savedTenant = res.data.tenant
      setTenant(savedTenant)
      loadTenant(savedTenant)
      if (tenantId === 'new') {
        toastSuccess('Tenant created')
        navigate(routes.tenantEdit(String(savedTenant.id)))
      } else {
        toastSuccess('Saved')
      }
    } else {
      toastError(res.error ?? 'Failed to save tenant')
    }
  }

  //--------------------------------------------------------------------------------
  const handleDelete = useCallback(async () => {
    if (!tenantId || tenantId === 'new' || !tenant) return
    if (!confirm('Delete this tenant? This cannot be undone.')) return
    const res = await api.request(tenantDeleteSpec, {
      path: { tenant: tenantId },
    })
    if (res.status === 200) {
      toastWarning('Tenant deleted')
      navigate(routes.home())
    } else {
      toastError(res.error ?? 'Failed to delete')
    }
  }, [tenantId, tenant, navigate])

  if (loading) return <main><p>Loading…</p></main>
  if (tenant === null && tenantId !== 'new') return <main><p>Tenant not found</p></main>

  //--------------------------------------------------------------------------------
  return (
    <BasicPage
      title={tenantId === 'new' ? 'New tenant' : name || 'Edit tenant'}
      className='TenantEditPage'
      toolbarButtons={<>
        <Button
          label='My account'
          icon='chevronLeft'
          disabled={tenantId === 'new'}
          href={routes.home(tenantId!)}
        />
        <Button
          label='Invoices'
          icon='file'
          disabled={tenantId === 'new'}
          href={routes.tenantInvoices(tenantId!)}
        />
        <Button
          variant='primary'
          label='Save'
          icon='save'
          disabled={!canEdit}
          onClick={() => handleSave()}
        />
        {canDelete && (
          <>
            <Button
              label={tenant?.disabled ? 'Disabled' : 'Enabled'}
              icon={tenant?.disabled ? 'regSquare' : 'checkSquare'}
              title={tenant?.disabled ? 'Enable tenant' : 'Disable tenant'}
              disabled={tenantId === 'new'}
              onClick={() => handleSave(tenant?.disabled ? 'enable' : 'disable')}
            />
            <Button
              label='Delete'
              icon='trash'
              disabled={tenantId === 'new'}
              onClick={handleDelete}
            />
          </>
        )}
        {userRole === UserRole.Admin && (
          <Button
            label='New tenant'
            icon='plus'
            disabled={!canEdit || tenantId === 'new'}
            href={routes.tenantEdit('new')}
          />
        )}
      </>}
    >

      {tenant?.disabled && (
        <Notice variant='warning' icon='exclamationTriangle'>
          This tenant is disabled.
        </Notice>
      )}

      <div className='FlexBricks'>

        <div className='Cutout Wrapped'>
          <h2>Basics</h2>
          <TextBox
            label='Tenant ID'
            readOnly
            width={120}
            value={tenant?.id != null ? String(tenant.id) : 'new'}
          />
          <TextBox
            label='Name'
            required
            width={320}
            value={name}
            setValue={setName}
          />
          <TimezoneBox
            label='Timezone'
            tooltip='e.g. America/New_York'
            required
            value={timezone}
            setValue={setTimezone}
          />
        </div>

        {tenantId && tenantId !== 'new' && (
          <>
            <TenantMembers tenantId={tenantId} />
            <TenantInvitations tenantId={tenantId} />
          </>
        )}

      </div>

    </BasicPage>
  )
}
